import { useState, useEffect, useCallback } from 'react';
import { useUseCelo } from '../../../providers/UseCeloProvider';
import type {
  InvoiceDisplay,
  UseBillingHistoryReturn
} from '../../../types/subscription-manager.types';
import { SubscriptionError } from '../../../types/subscription-manager.types';

/**
 * Custom hook for billing history management
 * Handles fetching invoices, pagination, and downloads
 */
export function useBillingHistory(): UseBillingHistoryReturn {
  const [invoices, setInvoices] = useState<InvoiceDisplay[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<SubscriptionError | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [cursor, setCursor] = useState<string | null>(null);

  const {
    paymentClient,
  } = useUseCelo();


  // Fetch invoices
  const fetchInvoices = useCallback(async (reset: boolean = true): Promise<void> => {
    if (!paymentClient) {
      setError(new SubscriptionError('Payment client not available', 'PAYMENT_CLIENT_MISSING', 400));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const startingCursor = reset ? undefined : cursor;
      const response = await paymentClient.getInvoices(undefined, 10, startingCursor || undefined);

      if (response && response.data) {
        // Transform to InvoiceDisplay format
        const displayInvoices: InvoiceDisplay[] = response.data.map((invoice: any) => ({
          ...invoice,
          number: invoice.number || invoice.id,
          description: invoice.description,
          items: invoice.items || [],
          downloadUrl: invoice.downloadUrl,
          hostedUrl: invoice.hostedUrl,
        }));

        if (reset) {
          setInvoices(displayInvoices);
        } else {
          setInvoices(prev => [...prev, ...displayInvoices]);
        }

        setHasMore(response.hasMore || false);
        setCursor(response.cursor || null);
      } else {
        if (reset) {
          setInvoices([]);
        }
        setHasMore(false);
        setCursor(null);
      }
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to fetch invoices',
            'FETCH_ERROR',
            500
          );
      setError(subscriptionError);

      if (reset) {
        setInvoices([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [paymentClient, cursor]);

  // Load more invoices (pagination)
  const loadMore = useCallback(async (): Promise<void> => {
    if (!hasMore || loading) {
      return;
    }

    await fetchInvoices(false);
  }, [hasMore, loading, fetchInvoices]);

  // Download invoice
  const downloadInvoice = useCallback(async (invoiceId: string): Promise<void> => {
    if (!paymentClient) {
      throw new SubscriptionError('Download invoice not available', 'DOWNLOAD_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      const result = await paymentClient.downloadInvoice(invoiceId);

      // If it returns a URL, open in new tab
      if ('url' in result && result.url) {
        window.open(result.url, '_blank');
      } else {
        throw new SubscriptionError('No download URL received', 'NO_DOWNLOAD_URL', 400);
      }
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to download invoice',
            'DOWNLOAD_ERROR',
            500
          );
      setError(subscriptionError);
      throw subscriptionError;
    }
  }, [paymentClient]);

  // View invoice in hosted page
  const viewInvoice = useCallback((invoice: InvoiceDisplay): void => {
    if (invoice.hostedUrl) {
      window.open(invoice.hostedUrl, '_blank');
    } else if (invoice.downloadUrl) {
      window.open(invoice.downloadUrl, '_blank');
    } else {
      const subscriptionError = new SubscriptionError(
        'No URL available for this invoice',
        'NO_INVOICE_URL',
        400
      );
      setError(subscriptionError);
      throw subscriptionError;
    }
  }, []);

  // Refresh invoices
  const refresh = useCallback(async (): Promise<void> => {
    setCursor(null);
    await fetchInvoices(true);
  }, [fetchInvoices]);

  // Get invoice by ID
  const getInvoiceById = useCallback((invoiceId: string): InvoiceDisplay | undefined => {
    return invoices.find(invoice => invoice.id === invoiceId);
  }, [invoices]);

  // Filter invoices by status
  const getInvoicesByStatus = useCallback((status: string): InvoiceDisplay[] => {
    return invoices.filter(invoice => invoice.status === status);
  }, [invoices]);

  // Get total amount for paid invoices
  const getTotalPaid = useCallback((): number => {
    return invoices
      .filter(invoice => invoice.status === 'paid')
      .reduce((total, invoice) => total + invoice.amountPaid, 0);
  }, [invoices]);

  // Get total outstanding amount
  const getTotalOutstanding = useCallback((): number => {
    return invoices
      .filter(invoice => ['open', 'past_due'].includes(invoice.status))
      .reduce((total, invoice) => total + invoice.amountDue, 0);
  }, [invoices]);

  // Load invoices on mount
  useEffect(() => {
    fetchInvoices(true);
  }, [fetchInvoices]);

  return {
    invoices,
    loading,
    error,
    hasMore,
    loadMore,
    downloadInvoice,
  };
}